'use client';
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { zoomRange } from '../constants/map';
// import algoliaIndex from '../hooks/algolia';

// Define the initial state and types
interface MapState {
    selectedLandInfo: any;
    mapCenterPos: { x: number; y: number };
    mapZoomLevel: number;
    landData: any;
    searchOptions: any;
    multiSelect: boolean;
}

const initialState: MapState = {
    selectedLandInfo: [],
    mapCenterPos: { x: 0, y: 0 },
    mapZoomLevel: 20,
    landData: {},
    searchOptions: {
        shiboshiZone: false,
        privatehub: false,
        diamond: false,
        platinum: false,
        gold: false,
        silver: false,
        openforbid: false,
        openforminting: false,
        minPos: { x: null, y: null },
        maxPos: { x: null, y: null },
        walletAddress: '',
    },
    multiSelect: false,
};

// Define actions
type Action =
    | { type: 'SET_SELECTED_LAND_INFO'; payload: { newLandInfo: any } }
    | { type: 'UPDATE_MAP_CENTER_POS'; payload: { newPos: { x: number; y: number } } }
    | { type: 'UPDATE_MAP_ZOOM_LEVEL'; payload: { newLevel: number } }
    | { type: 'UPDATE_LAND_DATA'; payload: { newLand: any } }
    | { type: 'UPDATE_SEARCH_OPTIONS'; payload: { newOptions: any } }
    | { type: 'UPDATE_MULTI_SELECT'; payload: { newValue: boolean } };

// Define reducer
function mapReducer(state: MapState, action: Action): MapState {
    switch (action.type) {
        case 'SET_SELECTED_LAND_INFO':
            return { ...state, selectedLandInfo: action.payload.newLandInfo };
        case 'UPDATE_MAP_CENTER_POS':
            return { ...state, mapCenterPos: action.payload.newPos };
        case 'UPDATE_MAP_ZOOM_LEVEL':
            const newZoomLevel = Math.max(Math.min(action.payload.newLevel, zoomRange.max), zoomRange.min);
            return { ...state, mapZoomLevel: newZoomLevel };
        case 'UPDATE_LAND_DATA':
            return { ...state, landData: action.payload.newLand };
        case 'UPDATE_SEARCH_OPTIONS':
            return { ...state, searchOptions: { ...state.searchOptions, ...action.payload.newOptions } };
        case 'UPDATE_MULTI_SELECT':
            return { ...state, multiSelect: action.payload.newValue };
        default:
            return state;
    }
}

// Create context
const MapContext = createContext<{ state: MapState; dispatch: React.Dispatch<Action> } | undefined>(undefined);

// Create provider
export const MapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(mapReducer, initialState);
    return (
        <MapContext.Provider value={{ state, dispatch }}>
            {children}
        </MapContext.Provider>
    );
};

// Custom hook to use the map context
export const useMapContext = () => {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error('useMapContext must be used within a MapProvider');
    }
    return context;
};